import React, { useEffect, useState } from 'react';
import { usePlayer } from '../../../../Contexts/playerData.context.jsx';
import { useNavBar } from '../../../../Contexts/navbar.items.context.jsx';
import { useDataLayer } from '../../../../Contexts/DataLayer.context.jsx';
import { buildAuthedFormBody } from '../../../../utils/functions.ts';
import { cflObject } from '../../../../utils/loadFunctions.ts';
import DragBox2026 from '../../../Universals/DragBox/DragBox2026/dragBox2026.component.jsx';
import './CFLRosterSummary.style.scss';

const columns = [
  { key: 'N', label: 'N', name: 'Nationals', color: 'var(--danger)' },
  { key: 'G', label: 'G', name: 'Globals', color: '#bae974', textColor: '#243247' },
  { key: 'U', label: 'A', name: 'Americans', color: 'var(--primary)' },
  { key: 'V', label: 'VET', name: 'Nationalized Americans', color: 'var(--brown)' },
];
const statusColumns = [
  { key: 'IR', name: 'Injured Reserve' },
  { key: 'I6', name: 'Injured - 6 Game' },
  { key: 'NA', name: 'Non-Active Roster' },
  { key: 'NL', name: 'Neg List' },
  { key: 'DR', name: 'Drafted Players' },
  { key: 'RO', name: 'Reserved Other: RT, DS, PU, AX, DN, SU, EX' },
];
const positions = ['QB', 'WR', 'RB', 'OL', 'DL', 'LB', 'DB', 'ST'];

export const rosterCounts = (players) => players.reduce((counts, player) => {
  const value = String(player.impnip ?? '').trim().toUpperCase();
  const key = value === 'N' || value === 'G' ? value : value.startsWith('V') ? 'V' : 'U';
  counts[key] += 1;
  const position = String(player.genPos ?? '').trim().toUpperCase();
  if (positions.includes(position)) counts[position] += 1;
  return counts;
}, { N: 0, G: 0, U: 0, V: 0, ...Object.fromEntries(positions.map(position => [position, 0])) });

export default function CFLRosterSummary() {
  const [rosters, setRosters] = useState(null);
  const [activeAllCounts, setActiveAllCounts] = useState(null);
  const [statusCounts, setStatusCounts] = useState(null);
  const [teamModes, setTeamModes] = useState({});
  const [selectedBreakdown, setSelectedBreakdown] = useState(null);
  const [allTeamsMode, setAllTeamsMode] = useState(null);
  const [error, setError] = useState('');
  const { setPlayerData, setSavedOriginalPlayerData, setPlayerID, setActivePlayer, setListLoadID, pendingRosterFilter } = usePlayer();
  const { updateActiveSideButton, updateSearchedListName } = useNavBar();
  const { updateWhichOneLiner } = useDataLayer();

  useEffect(() => {
    const controller = new AbortController();
    setRosters(null);
    setActiveAllCounts(null);
    setStatusCounts(null);
    setError('');
    const load = async () => {
      try {
        const response = await fetch('https://www.fbxchange.com/ReactPHP/S1Search.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: buildAuthedFormBody({ method: 'cflActiveRosters' }).toString(),
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok || !data.success || !data.rosters || !data.activeAllCounts || !data.statusCounts || cflObject.some(team => !Array.isArray(data.rosters[team.Team]) || !data.activeAllCounts[team.Team] || statusColumns.some(col => !data.statusCounts[team.Team]?.[col.key]))) {
          throw new Error('Unable to load CFL Active rosters. Please try again.');
        }
        if (!controller.signal.aborted) {
          setActiveAllCounts(data.activeAllCounts);
          setStatusCounts(data.statusCounts);
          setRosters(data.rosters);
        }
      } catch (err) {
        if (!controller.signal.aborted) setError(err.message);
      }
    };
    load();
    return () => controller.abort();
  }, []);

  const openRoster = (team) => {
    const players = rosters[team.Team];
    pendingRosterFilter.current = '';
    updateWhichOneLiner(null);
    setSavedOriginalPlayerData(players);
    setPlayerData(players);
    setPlayerID(players[0]?.FBXID || '');
    setActivePlayer(players[0] || null);
    updateSearchedListName(`${team.Team_Name}: Active Roster`);
    setListLoadID(`cfl-${team.Team}-${Date.now()}`);
    updateActiveSideButton('');
  };

  const getCounts = (team, mode) => mode === 'ALL'
    ? activeAllCounts[team.Team]
    : statusColumns.some(col => col.key === mode)
      ? statusCounts[team.Team][mode]
      : rosterCounts(rosters[team.Team]);
  const getTeamCounts = (team) => teamModes[team.Team] ? getCounts(team, teamModes[team.Team]) : null;
  const selectBreakdown = (team, mode) => {
    setAllTeamsMode(null);
    setTeamModes({ [team.Team]: mode });
    setSelectedBreakdown({ team, mode });
  };
  const selectAllTeams = (mode) => {
    setTeamModes(Object.fromEntries(cflObject.map(team => [team.Team, mode])));
    setSelectedBreakdown(null);
    setAllTeamsMode(mode);
  };
  const selectedCounts = rosters && selectedBreakdown
    ? getCounts(selectedBreakdown.team, selectedBreakdown.mode)
    : rosters && allTeamsMode
      ? cflObject.reduce((sum, team) => {
          const counts = getCounts(team, allTeamsMode);
          columns.forEach(col => { sum[col.key] += counts[col.key] ?? 0; });
          return sum;
        }, { N: 0, G: 0, U: 0, V: 0 })
      : null;
  const totals = rosters ? cflObject.reduce((sum, team) => {
    const counts = getTeamCounts(team);
    sum.ALL += activeAllCounts[team.Team].total;
    sum.AC += rosters[team.Team].length;
    statusColumns.forEach(col => { sum[col.key] += statusCounts[team.Team][col.key].total; });
    positions.forEach(position => { sum[position] += counts?.[position] ?? 0; });
    return sum;
  }, { ALL: 0, AC: 0, IR: 0, I6: 0, NA: 0, NL: 0, DR: 0, RO: 0, ...Object.fromEntries(positions.map(position => [position, 0])) }) : null;
  return (
    <DragBox2026 storageKey="CFL_ACTIVE_SUMMARY_POS" title="CFL Active Rosters" defaultPos={{ x: 80, y: 95 }} minWidth={1000} maxWidth={1200} className="card shadow-sm cfl-roster-summary-window">
      <div className="cfl-roster-summary">
        <div className="cfl-roster-summary-intro">
          <span>{selectedBreakdown ? `${selectedBreakdown.team.Team_Name} · ${selectedBreakdown.mode}` : allTeamsMode ? `All CFL teams · ${allTeamsMode}` : 'Click a roster count for a team’s breakdown.'}</span>
          <div className="cfl-roster-summary-actions">
            {columns.map(col => <button type="button" key={col.key} className="count color-count" style={{ '--count-color': col.color, '--count-text-color': col.textColor || '#fff' }} title={col.name} aria-label={col.label}>{selectedCounts ? selectedCounts[col.key] : col.label}</button>)}
          </div>
        </div>
        {!rosters && !error && <p role="status">Loading CFL teams…</p>}
        {error && <p role="alert" className="text-danger">{error}</p>}
        {rosters && <table>
          <colgroup>
            <col className="team-column" />
            {['ALL', 'AC', ...statusColumns.map(col => col.key), ...positions].map(key => <col key={key} className="number-column" />)}
          </colgroup>
          <thead><tr><th scope="col">Team</th>{[{ key: 'ALL', name: 'Active All' }, { key: 'AC', name: 'Active' }, ...statusColumns].map(col => <th scope="col" key={col.key}><button type="button" className="count roster-mode" title={col.name} aria-label={`${col.key} for all teams`} aria-pressed={cflObject.every(team => teamModes[team.Team] === col.key)} onClick={() => selectAllTeams(col.key)}>{col.key}</button></th>)}{positions.map(position => <th scope="col" key={position}>{position}</th>)}</tr></thead>
          <tbody>{cflObject.map(team => {
            const players = rosters[team.Team];
            const counts = getTeamCounts(team);
            return <tr key={team.Team}>
              <th scope="row"><button type="button" className="team-link" onClick={() => openRoster(team)}>{team.Team_Name}</button></th>
              <td><button type="button" className="count roster-mode" aria-pressed={selectedBreakdown?.team.Team === team.Team && selectedBreakdown.mode === 'ALL'} aria-label={`${team.Team_Name}: Active All breakdown`} onClick={() => selectBreakdown(team, 'ALL')}>{activeAllCounts[team.Team].total}</button></td>
              <td><button type="button" className="count roster-mode" aria-pressed={selectedBreakdown?.team.Team === team.Team && selectedBreakdown.mode === 'AC'} aria-label={`${team.Team_Name}: Active breakdown`} onClick={() => selectBreakdown(team, 'AC')}>{players.length}</button></td>
              {statusColumns.map(col => <td key={col.key}><button type="button" className="count roster-mode" aria-pressed={selectedBreakdown?.team.Team === team.Team && selectedBreakdown.mode === col.key} aria-label={`${team.Team_Name}: ${col.key} breakdown`} onClick={() => selectBreakdown(team, col.key)}>{statusCounts[team.Team][col.key].total}</button></td>)}
              {positions.map(position => <td key={position}>{counts?.[position] || '-'}</td>)}
            </tr>;
          })}</tbody>
          <tfoot><tr><th scope="row">CFL Total</th><td>{totals.ALL}</td><td>{totals.AC}</td>{statusColumns.map(col => <td key={col.key}>{totals[col.key]}</td>)}{positions.map(position => <td key={position}>{Object.keys(teamModes).length && totals[position] ? totals[position] : '-'}</td>)}</tr></tfoot>
        </table>}
      </div>
    </DragBox2026>
  );
}
